import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axios from "axios"; // Importar axios diretamente

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Tab,
  Tabs,
  FormGroup,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme
} from "@material-ui/core";

import {
  DeleteOutline as DeleteOutlineIcon,
  Send as SendIcon,
  Code as CodeIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  GetApp as GetAppIcon,
  PostAdd as PostAddIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwapHorizIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { Stack } from "@mui/material";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  requestForm: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  methodSelect: {
    minWidth: 120,
  },
  urlField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
  },
  responseContainer: {
    marginTop: theme.spacing(3),
    maxHeight: 400,
    overflow: "auto",
  },
  responseHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  successChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  errorChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  warningChip: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  historyItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  paramRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  codeEditor: {
    fontFamily: "'Fira Code', 'Consolas', monospace",
    fontSize: 14,
    lineHeight: 1.5,
  },
  tabContent: {
    padding: theme.spacing(2),
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
}));

const RequestSchema = Yup.object().shape({
  url: Yup.string()
    .url("URL inválida")
    .required("URL é obrigatória"),
  method: Yup.string()
    .oneOf(["GET", "POST", "PUT", "DELETE", "PATCH"], "Método inválido")
    .required("Método é obrigatório"),
});

const FlowBuilderAddRequestModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [labels, setLabels] = useState({
    title: "Adicionar requisição API",
    btn: "Adicionar",
    btnTest: "Testar API"
  });

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [requestHistory, setRequestHistory] = useState([]);
  const [headers, setHeaders] = useState([
    { key: "Content-Type", value: "application/json", enabled: true }
  ]);
  const [queryParams, setQueryParams] = useState([]);
  const [bodyType, setBodyType] = useState("json");
  const [body, setBody] = useState("{\n  \n}");
  const [formData, setFormData] = useState([{ key: "", value: "", enabled: true }]);
  const [rawBody, setRawBody] = useState("");
  const [showResponseDetails, setShowResponseDetails] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [formValues, setFormValues] = useState({
    method: "GET",
    url: "https://jsonplaceholder.typicode.com/posts",
  });

  const httpMethods = [
    { value: "GET", label: "GET", color: "primary" },
    { value: "POST", label: "POST", color: "primary" },
    { value: "PUT", label: "PUT", color: "primary" },
    { value: "DELETE", label: "DELETE", color: "primary" },
    { value: "PATCH", label: "PATCH", color: "primary" },
  ];

  const bodyTypes = [
    { value: "json", label: "JSON" },
    { value: "form-data", label: "Form Data" },
    { value: "raw", label: "Raw" },
  ];

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar requisição API",
        btn: "Salvar",
        btnTest: "Testar API"
      });
      if (data && data.data) {
        const nodeData = data.data;
        setFormValues({
          method: nodeData.method || "GET",
          url: nodeData.url || "https://jsonplaceholder.typicode.com/posts",
        });
        setBody(nodeData.body || "{\n  \n}");
        setBodyType(nodeData.bodyType || "json");
        setHeaders(nodeData.headers || [{ key: "Content-Type", value: "application/json", enabled: true }]);
        setQueryParams(nodeData.queryParams || []);
        if (nodeData.formData) {
          setFormData(nodeData.formData);
        }
        if (nodeData.rawBody) {
          setRawBody(nodeData.rawBody);
        }
      }
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar requisição API",
        btn: "Adicionar",
        btnTest: "Testar API"
      });
      setFormValues({
        method: "GET",
        url: "https://jsonplaceholder.typicode.com/posts",
      });
      setBody("{\n  \n}");
      setBodyType("json");
      setHeaders([{ key: "Content-Type", value: "application/json", enabled: true }]);
      setQueryParams([]);
      setFormData([{ key: "", value: "", enabled: true }]);
      setRawBody("");
      setResponse(null);
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }

    const savedHistory = localStorage.getItem("apiTesterHistory");
    if (savedHistory) {
      setRequestHistory(JSON.parse(savedHistory));
    }
  }, [open, data]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close?.(null);
    setActiveModal(false);
    setTestMode(false);
    setResponse(null);
  };

  const addHeader = () => {
    setHeaders([...headers, { key: "", value: "", enabled: true }]);
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  const removeHeader = (index) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const addQueryParam = () => {
    setQueryParams([...queryParams, { key: "", value: "", enabled: true }]);
  };

  const updateQueryParam = (index, field, value) => {
    const newParams = [...queryParams];
    newParams[index][field] = value;
    setQueryParams(newParams);
  };

  const removeQueryParam = (index) => {
    setQueryParams(queryParams.filter((_, i) => i !== index));
  };

  const addFormData = () => {
    setFormData([...formData, { key: "", value: "", enabled: true }]);
  };

  const updateFormData = (index, field, value) => {
    const newFormData = [...formData];
    newFormData[index][field] = value;
    setFormData(newFormData);
  };

  const removeFormData = (index) => {
    setFormData(formData.filter((_, i) => i !== index));
  };

  const buildUrlWithParams = (baseUrl) => {
    const enabledParams = queryParams.filter(param => param.enabled && param.key);
    if (enabledParams.length === 0) return baseUrl;

    const params = new URLSearchParams();
    enabledParams.forEach(param => {
      params.append(param.key, param.value);
    });

    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${params.toString()}`;
  };

  const buildHeadersObject = () => {
    const headersObj = {};
    headers
      .filter(header => header.enabled && header.key)
      .forEach(header => {
        headersObj[header.key] = header.value;
      });
    return headersObj;
  };

  const buildBodyData = () => {
    switch (bodyType) {
      case "json":
        try {
          return body ? JSON.parse(body) : {};
        } catch (e) {
          toast.error("JSON inválido");
          return {};
        }
      case "form-data":
        const formDataObj = new FormData();
        formData
          .filter(item => item.enabled && item.key)
          .forEach(item => {
            formDataObj.append(item.key, item.value);
          });
        return formDataObj;
      case "raw":
        return rawBody;
      default:
        return {};
    }
  };

  const sendTestRequest = async () => {
    setLoading(true);
    setResponse(null);
    setResponseTime(null);

    const startTime = Date.now();

    try {
      const url = buildUrlWithParams(formValues.url);
      const headersObj = buildHeadersObject();
      const requestData = ["GET", "DELETE"].includes(formValues.method) ? undefined : buildBodyData();

      const config = {
        method: formValues.method.toLowerCase(),
        url: url,
        headers: headersObj,
        data: requestData,
        withCredentials: false,
      };

      if (bodyType === "form-data" && headersObj["Content-Type"]) {
        delete config.headers["Content-Type"];
      }

      const response = await axios(config);
      const endTime = Date.now();

      setResponse({
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
      setResponseTime(endTime - startTime);

      const requestDataObj = {
        id: Date.now(),
        method: formValues.method,
        url: formValues.url,
        timestamp: new Date().toISOString(),
        status: response.status,
        success: response.status >= 200 && response.status < 300,
      };

      const newHistory = [requestDataObj, ...requestHistory.slice(0, 9)];
      setRequestHistory(newHistory);
      localStorage.setItem("apiTesterHistory", JSON.stringify(newHistory));

      toast.success(`Requisição ${formValues.method} realizada com sucesso!`);
    } catch (error) {
      const endTime = Date.now();
      setResponseTime(endTime - startTime);

      setResponse({
        error: true,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers,
      });

      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRequest = () => {
    const requestData = {
      method: formValues.method,
      url: formValues.url,
      headers: headers.filter(h => h.key && h.value),
      queryParams: queryParams.filter(p => p.key && p.value),
      body: bodyType === 'json' ? body : (bodyType === 'raw' ? rawBody : ''),
      bodyType: bodyType,
      formData: bodyType === 'form-data' ? formData.filter(f => f.key && f.value) : [],
      response: response,
      responseTime: responseTime,
      lastExecuted: new Date().toISOString(),
      label: `${formValues.method} ${formValues.url.substring(0, 30)}...`
    };

    if (open === "edit") {
      handleClose();
      onUpdate?.({
        ...data,
        data: requestData
      });
    } else if (open === "create") {
      handleClose();
      onSave?.(requestData);
    }
  };

  const loadRequestFromHistory = (request) => {
    setFormValues({
      method: request.method,
      url: request.url,
    });
    setActiveTab(0);
    toast.info("Requisição carregada do histórico");
  };

  const formatJSON = (data) => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return "default";
    if (status >= 200 && status < 300) return "primary";
    if (status >= 400 && status < 500) return "secondary";
    return "default";
  };

  const getChipClassName = (status) => {
    if (!status) return "";
    if (status >= 200 && status < 300) return classes.successChip;
    if (status >= 400 && status < 500) return classes.warningChip;
    return classes.errorChip;
  };

  const handleTestRequest = () => {
    if (!formValues.url) {
      toast.error("URL é obrigatória");
      return;
    }
    setTestMode(true);
    sendTestRequest();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSaveRequest();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title" style={{ padding: "16px 24px", borderBottom: theme.palette.type === 'dark' ? "1px solid #333" : "1px solid #eee", backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#fff"  }}>
          {labels.title}
        </DialogTitle>
        <Stack>
          <DialogContent dividers>
            <div className={classes.requestForm}>
              {/* Linha de Método e URL */}
              <div className={classes.formRow}>
                <FormControl variant="outlined" size="small" className={classes.methodSelect}>
                  <InputLabel>Método</InputLabel>
                  <Select
                    name="method"
                    value={formValues.method}
                    onChange={handleChange}
                    label="Método"
                  >
                    {httpMethods.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  name="url"
                  label="URL"
                  variant="outlined"
                  size="small"
                  value={formValues.url}
                  onChange={handleChange}
                  className={classes.urlField}
                  placeholder="https://api.example.com/endpoint"
                  required
                />

                <Button
                  variant="outlined"
                  style={{
                    color: "white",
                    backgroundColor: theme.palette.type === 'dark' ? "#4c1d95" : "#8E2DE2",
                    boxShadow: "none",
                    borderRadius: 4,
                  }}
                  disabled={loading}
                  startIcon={<SendIcon />}
                  className={classes.btnWrapper}
                  onClick={handleTestRequest}
                >
                  {labels.btnTest}
                  {loading && testMode && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </div>

              {/* Tabs para Parâmetros, Headers, Body */}
              <Paper elevation={1}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  <Tab label="Parâmetros" />
                  <Tab label="Headers" />
                  <Tab label="Body" />
                  <Tab label="Histórico" />
                </Tabs>

                {/* Conteúdo da Tab Parâmetros */}
                {activeTab === 0 && (
                  <div className={classes.tabContent}>
                    {queryParams.map((param, index) => (
                      <div key={index} className={classes.paramRow}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={param.enabled}
                              onChange={(e) => updateQueryParam(index, "enabled", e.target.checked)}
                              size="small"
                            />
                          }
                        />
                        <TextField
                          label="Chave"
                          size="small"
                          value={param.key}
                          onChange={(e) => updateQueryParam(index, "key", e.target.value)}
                          variant="outlined"
                          style={{ flex: 1 }}
                        />
                        <TextField
                          label="Valor"
                          size="small"
                          value={param.value}
                          onChange={(e) => updateQueryParam(index, "value", e.target.value)}
                          variant="outlined"
                          style={{ flex: 2 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeQueryParam(index)}
                          disabled={queryParams.length === 1}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                    <Button
                      size="small"
                      onClick={addQueryParam}
                      startIcon={<PostAddIcon />}
                    >
                      Adicionar Parâmetro
                    </Button>
                  </div>
                )}

                {/* Conteúdo da Tab Headers */}
                {activeTab === 1 && (
                  <div className={classes.tabContent}>
                    {headers.map((header, index) => (
                      <div key={index} className={classes.paramRow}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={header.enabled}
                              onChange={(e) => updateHeader(index, "enabled", e.target.checked)}
                              size="small"
                            />
                          }
                        />
                        <TextField
                          label="Chave"
                          size="small"
                          value={header.key}
                          onChange={(e) => updateHeader(index, "key", e.target.value)}
                          variant="outlined"
                          style={{ flex: 1 }}
                        />
                        <TextField
                          label="Valor"
                          size="small"
                          value={header.value}
                          onChange={(e) => updateHeader(index, "value", e.target.value)}
                          variant="outlined"
                          style={{ flex: 2 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeHeader(index)}
                          disabled={headers.length === 1}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                    <Button
                      size="small"
                      onClick={addHeader}
                      startIcon={<PostAddIcon />}
                    >
                      Adicionar Header
                    </Button>
                  </div>
                )}

                {/* Conteúdo da Tab Body */}
                {activeTab === 2 && (
                  <div className={classes.tabContent}>
                    <FormControl variant="outlined" size="small" style={{ marginBottom: 16 }}>
                      <InputLabel>Tipo de Body</InputLabel>
                      <Select
                        value={bodyType}
                        onChange={(e) => setBodyType(e.target.value)}
                        label="Tipo de Body"
                      >
                        {bodyTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {bodyType === "json" && (
                      <TextField
                        multiline
                        rows={8}
                        variant="outlined"
                        fullWidth
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        className={classes.codeEditor}
                        placeholder='{"key": "value"}'
                      />
                    )}

                    {bodyType === "form-data" && (
                      <div>
                        {formData.map((item, index) => (
                          <div key={index} className={classes.paramRow}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={item.enabled}
                                  onChange={(e) => updateFormData(index, "enabled", e.target.checked)}
                                  size="small"
                                />
                              }
                            />
                            <TextField
                              label="Chave"
                              size="small"
                              value={item.key}
                              onChange={(e) => updateFormData(index, "key", e.target.value)}
                              variant="outlined"
                              style={{ flex: 1 }}
                            />
                            <TextField
                              label="Valor"
                              size="small"
                              value={item.value}
                              onChange={(e) => updateFormData(index, "value", e.target.value)}
                              variant="outlined"
                              style={{ flex: 2 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeFormData(index)}
                              disabled={formData.length === 1}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </div>
                        ))}
                        <Button
                          size="small"
                          onClick={addFormData}
                          startIcon={<PostAddIcon />}
                        >
                          Adicionar Campo
                        </Button>
                      </div>
                    )}

                    {bodyType === "raw" && (
                      <TextField
                        multiline
                        rows={8}
                        variant="outlined"
                        fullWidth
                        value={rawBody}
                        onChange={(e) => setRawBody(e.target.value)}
                        className={classes.codeEditor}
                        placeholder="Digite o corpo da requisição..."
                      />
                    )}
                  </div>
                )}

                {/* Conteúdo da Tab Histórico */}
                {activeTab === 3 && (
                  <div className={classes.tabContent}>
                    {requestHistory.length === 0 ? (
                      <Typography color="textSecondary" align="center">
                        Nenhuma requisição no histórico
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Método</TableCell>
                              <TableCell>URL</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Horário</TableCell>
                              <TableCell align="right">Ações</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {requestHistory.map((request) => (
                              <TableRow
                                key={request.id}
                                className={classes.historyItem}
                                hover
                              >
                                <TableCell>
                                  <Chip
                                    label={request.method}
                                    size="small"
                                    color="primary"
                                    style={{ minWidth: 70 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    style={{
                                      fontFamily: "'Fira Code', monospace",
                                      fontSize: 12,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: 300
                                    }}
                                  >
                                    {request.url}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={request.status}
                                    size="small"
                                    className={getChipClassName(request.status)}
                                    style={{
                                      color: 'white',
                                      backgroundColor: request.success ? (theme.palette.type === 'dark' ? '#2e7d32' : '#4caf50') : (theme.palette.type === 'dark' ? '#c62828' : '#f44336')
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">
                                    {new Date(request.timestamp).toLocaleTimeString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={() => loadRequestFromHistory(request)}
                                  >
                                    <GetAppIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const newHistory = requestHistory.filter(r => r.id !== request.id);
                                      setRequestHistory(newHistory);
                                      localStorage.setItem("apiTesterHistory", JSON.stringify(newHistory));
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </div>
                )}
              </Paper>

              {/* Área de Resposta */}
              {response && (
                <div className={classes.responseContainer}>
                  <Card variant="outlined">
                    <CardContent>
                      <div className={classes.responseHeader}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="h6">
                            Resposta
                          </Typography>
                          {response.status && (
                            <Chip
                              label={`${response.status} ${response.statusText || ""}`}
                              className={getChipClassName(response.status)}
                              size="small"
                              style={{
                                marginLeft: 8,
                                color: 'white',
                                backgroundColor: response.status >= 200 && response.status < 300
                                  ? (theme.palette.type === 'dark' ? '#2e7d32' : '#4caf50')
                                  : response.status >= 400 && response.status < 500
                                    ? (theme.palette.type === 'dark' ? '#ef6c00' : '#ff9800')
                                    : (theme.palette.type === 'dark' ? '#c62828' : '#f44336')
                              }}
                            />
                          )}
                          {responseTime && (
                            <Chip
                              label={`${responseTime}ms`}
                              size="small"
                              variant="outlined"
                              style={{ marginLeft: 8 }}
                            />
                          )}
                        </div>
                        <IconButton
                          size="small"
                          onClick={() => setShowResponseDetails(!showResponseDetails)}
                        >
                          {showResponseDetails ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </div>

                      {showResponseDetails && response.headers && (
                        <div style={{ marginBottom: 16 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Headers:
                          </Typography>
                          <Paper variant="outlined" style={{ padding: 8 }}>
                            <pre style={{ margin: 0, fontSize: 12 }}>
                              {JSON.stringify(response.headers, null, 2)}
                            </pre>
                          </Paper>
                        </div>
                      )}

                      <Typography variant="subtitle2" gutterBottom>
                        Body:
                      </Typography>
                      <Paper
                        variant="outlined"
                        style={{
                          padding: 16,
                          backgroundColor: theme.palette.type === 'dark' ? "#333333" : "#f5f5f5",
                          maxHeight: 300,
                          overflow: "auto",
                        }}
                      >
                        <pre style={{ margin: 0, fontSize: 12 }}>
                          {response.error
                            ? `Erro: ${response.message}\n\n${response.data ? formatJSON(response.data) : ""}`
                            : formatJSON(response.data)}
                        </pre>
                      </Paper>

                      {/* Botões de ação para resposta */}
                      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CodeIcon />}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              JSON.stringify(response.data, null, 2)
                            );
                            toast.success("JSON copiado para área de transferência!");
                          }}
                        >
                          Copiar JSON
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<FormatAlignLeftIcon />}
                          onClick={() => {
                            if (response.data && bodyType === 'json') {
                              try {
                                const formatted = JSON.stringify(response.data, null, 2);
                                setBody(formatted);
                                setBodyType('json');
                                toast.success("Resposta usada como body!");
                              } catch (e) {
                                toast.error("Não foi possível usar a resposta como body");
                              }
                            }
                          }}
                        >
                          Usar como Body
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleClose} 
              style={{
                color: "white",
                backgroundColor: theme.palette.type === 'dark' ? "#c62828" : "#db6565",
                boxShadow: "none",
                borderRadius: 4,
              }}
              variant="outlined"
            >
              {i18n.t("contactModal.buttons.cancel")}
            </Button>
            <Button
              type="button"
              style={{
                color: "white",
                backgroundColor: theme.palette.type === 'dark' ? "#4c1d95" : "#8E2DE2",
                boxShadow: "none",
                borderRadius: 4,
              }}
              variant="contained"
              className={classes.btnWrapper}
              onClick={handleSubmit}
            >
              {labels.btn}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddRequestModal;